/**
 * Iter-149 · Section 42 — Public Demo Mode.
 *
 * A no-auth public exploration of the NECS Integrated Facility Care
 * platform. Prospects can preview:
 *   1. The 12-vertical commercial quote engine (read-only wizard shell)
 *   2. Sample facility profiles for Standard Commercial + Strategic Enterprise
 *   3. Sample customer portal (contracts, invoices, payments — placeholder data)
 *   4. Multifamily resident experience (read-only sample)
 *   5. The 3-tier Essential / Professional / Executive selector per vertical
 *
 * Explicit rules from spec:
 *   - No form submissions, no payment entry, no contract creation.
 *   - No sign-in required.
 *   - All demo data clearly labeled as sample / demo.
 *   - Fully mobile-responsive.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const STRATEGIC_ENTERPRISE = new Set(['bank', 'stadium', 'airport']);

const LINE_ICON = {
  office: '🏬', retail: '🛍️', gas_station: '⛽',
  school: '🏫', medical: '🏥', warehouse: '🏭',
  restaurant: '🍽️', mall: '🏦', church: '⛪',
  bank: '🏦', stadium: '🏟️', airport: '✈️',
};

const QUOTE_STEPS = [
  '1. Select facility type',
  '2. Facility information',
  '3. Square footage',
  '4. Operating hours',
  '5. Service frequency',
  '6. Facility zones',
  '7. Core services',
  '8. Package (Essential / Professional / Executive)',
  '9. Specialty services',
  '10. Operational complexity',
  '11. Traffic / utilization',
  '12. Contract duration (12 / 24 / 36 months)',
  '13. Preliminary estimate',
  '14. Facility assessment',
  '15. Final proposal',
  '16. Contract',
  '17. Payment setup (Stripe / ACH)',
  '18. Onboarding',
];

// Iter-150 · Red Team Fix 7 — sample contracts, invoices and payment
// data per property type so the Customer Portal panel demonstrates the
// full billing architecture for every vertical (Multifamily + all 12
// commercial). Includes Stripe/ACH + QuickBooks reconciliation hints
// and NET 30 terms per Section 22-25 of the Master Spec.
const SAMPLE_ACCOUNT_DATA = {
  residential: {
    service_line: 'NECS Valet',
    facility: 'Sunset Vista Apartments',
    package: 'Professional Collection',
    duration_months: 12,
    monthly_rate: '$5,760.00',
    payment_method: 'ACH',
    stripe_status: 'Test-mode ready',
    quickbooks_status: 'GL-mapped: 4000-VALET',
    net_terms: 'Net 30 · 5-day courtesy',
  },
  office:      { service_line: 'NECS Office Pro',   facility: 'Summit Corporate Tower',   package: 'Professional Care', duration_months: 24, monthly_rate: '$4,285.00', payment_method: 'ACH',  stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4100-OFFICE',   net_terms: 'Net 30 · 5-day courtesy' },
  retail:      { service_line: 'NECS Retail Clean', facility: 'Lakeside Commons Plaza',   package: 'Essential Care',    duration_months: 12, monthly_rate: '$3,120.00', payment_method: 'ACH',  stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4110-RETAIL',   net_terms: 'Net 30 · 5-day courtesy' },
  gas_station: { service_line: 'NECS Station Care', facility: 'Lone Star Fuel & Go #12',  package: 'Professional Care', duration_months: 12, monthly_rate: '$1,890.00', payment_method: 'Card', stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4120-STATION',  net_terms: 'Net 30 · 5-day courtesy' },
  school:      { service_line: 'NECS Campus Clean', facility: 'Heritage Prep Academy',    package: 'Executive Care',    duration_months: 36, monthly_rate: '$8,450.00', payment_method: 'ACH',  stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4130-CAMPUS',   net_terms: 'Net 30 · 5-day courtesy' },
  medical:     { service_line: 'NECS MedSafe',      facility: 'Trinity Family Medical',   package: 'Executive Care',    duration_months: 24, monthly_rate: '$6,220.00', payment_method: 'ACH',  stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4140-MEDICAL',  net_terms: 'Net 30 · 5-day courtesy' },
  warehouse:   { service_line: 'NECS Industrial',   facility: 'Gateway Distribution Hub', package: 'Professional Care', duration_months: 24, monthly_rate: '$7,940.00', payment_method: 'ACH',  stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4150-INDUSTRIAL', net_terms: 'Net 30 · 5-day courtesy' },
  restaurant:  { service_line: 'NECS DineClean',    facility: 'Brazos Grill & Cantina',   package: 'Essential Care',    duration_months: 12, monthly_rate: '$2,650.00', payment_method: 'ACH',  stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4160-DINE',     net_terms: 'Net 30 · 5-day courtesy' },
  mall:        { service_line: 'NECS MallCare',     facility: 'Northgate Galleria',       package: 'Executive Care',    duration_months: 36, monthly_rate: '$18,900.00', payment_method: 'ACH', stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4170-MALL',     net_terms: 'Net 30 · 5-day courtesy' },
  church:      { service_line: 'NECS ChurchCare',   facility: 'Grace Fellowship Church',  package: 'Professional Care', duration_months: 12, monthly_rate: '$3,540.00', payment_method: 'ACH',  stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4180-CHURCH',   net_terms: 'Net 30 · 5-day courtesy' },
  // Strategic Enterprise — 36-month agreements + ACH primary.
  bank:        { service_line: 'NECS BankCare',    facility: 'First Trinity Financial Center', package: 'Executive Care', duration_months: 36, monthly_rate: '$9,600.00', payment_method: 'ACH', stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4200-BANK',    net_terms: 'Net 30 · 5-day courtesy' },
  stadium:     { service_line: 'NECS StadiumCare', facility: 'Lone Star Arena',                package: 'Executive Care', duration_months: 36, monthly_rate: '$42,750.00', payment_method: 'ACH', stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4210-STADIUM', net_terms: 'Net 30 · 5-day courtesy' },
  airport:     { service_line: 'NECS AirportCare', facility: 'Metroplex Regional (Terminal B)', package: 'Executive Care', duration_months: 36, monthly_rate: '$68,400.00', payment_method: 'ACH', stripe_status: 'Test-mode ready', quickbooks_status: 'GL-mapped: 4220-AIRPORT', net_terms: 'Net 30 · 5-day courtesy' },
};

function buildAccountSample(ptype) {
  const base = SAMPLE_ACCOUNT_DATA[ptype] || SAMPLE_ACCOUNT_DATA.office;
  return {
    ...base,
    invoices: [
      { id: `INV-DEMO-${ptype.toUpperCase().slice(0, 4)}-4021`, month: 'Feb 2026', amount: base.monthly_rate, status: 'Paid' },
      { id: `INV-DEMO-${ptype.toUpperCase().slice(0, 4)}-4020`, month: 'Jan 2026', amount: base.monthly_rate, status: 'Paid' },
      { id: `INV-DEMO-${ptype.toUpperCase().slice(0, 4)}-4019`, month: 'Dec 2025', amount: base.monthly_rate, status: 'Paid' },
    ],
    payments: [
      { date: 'Feb 05', method: base.payment_method, amount: base.monthly_rate, reference: `PAY-DEMO-${ptype.toUpperCase().slice(0, 4)}-2214` },
      { date: 'Jan 04', method: base.payment_method, amount: base.monthly_rate, reference: `PAY-DEMO-${ptype.toUpperCase().slice(0, 4)}-2199` },
    ],
  };
}

export default function PlatformDemoPage() {
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [activeType, setActiveType] = useState('office');
  const [selectedTier, setSelectedTier] = useState('standard');
  const [activePanel, setActivePanel] = useState('quote'); // quote | facility | portal | resident

  useEffect(() => {
    fetch(`${API}/api/commercial/service-lines?visibility=commercial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.service_lines) setLines(d.service_lines); })
      .catch(() => {});

    // Section 43.E — allow the demo dashboard reset button to reset the
    // in-page quote demo to Step 1.
    const onReset = () => {
      setActiveType('office');
      setSelectedTier('standard');
      setActivePanel('quote');
    };
    window.addEventListener('necs:reset-quote-demo', onReset);
    return () => window.removeEventListener('necs:reset-quote-demo', onReset);
  }, []);

  const active = useMemo(
    () => lines.find((l) => l.property_type === activeType) || null,
    [lines, activeType],
  );

  const standardLines   = lines.filter((l) => !STRATEGIC_ENTERPRISE.has(l.property_type));
  const strategicLines  = lines.filter((l) =>  STRATEGIC_ENTERPRISE.has(l.property_type));

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#111' }} data-testid="platform-demo-page">
      {/* Header */}
      <header className="border-b" style={{ borderColor: '#3f3f46', backgroundColor: '#1f1f1f' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-black" style={{ color: '#C9A962' }}>
              NECS · Integrated Facility Care
            </p>
            <h1 className="text-xl sm:text-2xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Explore the Platform · Public Demo
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-widest"
              style={{ borderColor: '#fbbf24', color: '#fbbf24' }}
              data-testid="demo-mode-badge"
            >
              Demo Mode · Sample Data
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              data-testid="demo-back-home"
            >
              Back to Site
            </Button>
          </div>
        </div>
      </header>

      {/* Panel selector */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 pt-6" data-testid="demo-panel-nav">
        <div className="flex flex-wrap gap-2">
          {[
            { k: 'quote',    label: 'Quote Engine' },
            { k: 'facility', label: 'Facility Profile' },
            { k: 'portal',   label: 'Customer Portal' },
            { k: 'resident', label: 'Multifamily Resident' },
          ].map((p) => (
            <button
              key={p.k}
              type="button"
              onClick={() => setActivePanel(p.k)}
              className="px-4 py-2 text-xs font-bold uppercase tracking-widest rounded transition-colors"
              style={{
                backgroundColor: activePanel === p.k ? '#C9A962' : 'transparent',
                color: activePanel === p.k ? '#0a0a0a' : '#e5e5e5',
                border: `1px solid ${activePanel === p.k ? '#C9A962' : '#3f3f46'}`,
              }}
              data-testid={`demo-panel-tab-${p.k}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* PANEL: QUOTE ENGINE */}
        {activePanel === 'quote' && (
          <Card className="p-5 sm:p-6" style={{ backgroundColor: '#1a1a1a', border: '1px solid #3f3f46' }}>
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: '#22d3ee' }}>
                Commercial Quote Engine · 18 Steps
              </p>
              <h2 className="text-2xl font-black text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Preview the Quote Flow
              </h2>
              <p className="text-sm text-zinc-400">
                Every commercial vertical follows the same 18-step engine. Pick a facility type
                below to see zones, package options and pricing inputs specific to that industry.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Vertical picker */}
              <div className="lg:col-span-1">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
                  Standard Commercial · 1-9
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4" data-testid="demo-standard-verticals">
                  {standardLines.map((l) => (
                    <button
                      key={l.property_type}
                      type="button"
                      onClick={() => setActiveType(l.property_type)}
                      className="text-left rounded p-2 transition-colors"
                      style={{
                        backgroundColor: activeType === l.property_type ? 'rgba(34,211,238,0.15)' : '#0f0f0f',
                        border: `1px solid ${activeType === l.property_type ? '#22d3ee' : '#3f3f46'}`,
                      }}
                      data-testid={`demo-vertical-${l.property_type}`}
                    >
                      <p className="text-lg leading-none">{LINE_ICON[l.property_type] || '🧼'}</p>
                      <p className="text-[10px] font-bold text-white mt-1 leading-tight">
                        {l.necs_name}
                      </p>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#f59e0b' }}>
                  Strategic Enterprise · 10-12
                </p>
                <div className="grid grid-cols-3 gap-2" data-testid="demo-strategic-verticals">
                  {strategicLines.map((l) => (
                    <button
                      key={l.property_type}
                      type="button"
                      onClick={() => setActiveType(l.property_type)}
                      className="text-left rounded p-2 transition-colors"
                      style={{
                        backgroundColor: activeType === l.property_type ? 'rgba(245,158,11,0.15)' : '#0f0f0f',
                        border: `1px solid ${activeType === l.property_type ? '#f59e0b' : '#3f3f46'}`,
                      }}
                      data-testid={`demo-vertical-${l.property_type}`}
                    >
                      <p className="text-lg leading-none">{LINE_ICON[l.property_type] || '🧼'}</p>
                      <p className="text-[10px] font-bold text-white mt-1 leading-tight">
                        {l.necs_name}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Vertical detail + tier picker */}
              <div className="lg:col-span-2">
                {active ? (
                  <>
                    <div className="mb-4">
                      <p className="text-[10px] uppercase tracking-widest font-black flex items-center gap-2" style={{ color: STRATEGIC_ENTERPRISE.has(activeType) ? '#f59e0b' : '#22d3ee' }}>
                        <span aria-hidden="true">{LINE_ICON[activeType] || '🧼'}</span>
                        {active.necs_name} · {STRATEGIC_ENTERPRISE.has(activeType) ? 'Strategic Enterprise' : 'Standard Commercial'}
                      </p>
                      <p className="text-sm text-zinc-300 mt-1">{active.tagline}</p>
                      {active.disclaimer && (
                        <p className="text-[11px] text-amber-300 mt-2 italic border-l-2 border-amber-500 pl-2">
                          {active.disclaimer}
                        </p>
                      )}
                    </div>

                    {/* Zones */}
                    {Array.isArray(active.zones) && active.zones.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
                          Facility Zones ({active.zones.length})
                        </p>
                        <div className="flex flex-wrap gap-1" data-testid="demo-zones">
                          {active.zones.map((z) => (
                            <span
                              key={z.key}
                              className="text-[10px] px-2 py-1 rounded"
                              style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46', color: '#e5e5e5' }}
                              title={z.detail}
                            >
                              Zone {z.key} · {z.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Priority order */}
                    {Array.isArray(active.priority_order) && active.priority_order.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
                          Service Priority Order
                        </p>
                        <p className="text-xs text-zinc-300">
                          {active.priority_order.join(' → ')}
                        </p>
                      </div>
                    )}

                    {/* Tier selector */}
                    <div className="mb-4">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
                        Package · Essential / Professional / Executive
                      </p>
                      <div className="grid grid-cols-3 gap-2" data-testid="demo-tier-selector">
                        {(active.tiers || []).map((t) => (
                          <button
                            key={t.key}
                            type="button"
                            onClick={() => setSelectedTier(t.key)}
                            className="rounded p-3 text-left transition-colors"
                            style={{
                              backgroundColor: selectedTier === t.key ? 'rgba(201,169,98,0.15)' : '#0f0f0f',
                              border: `1px solid ${selectedTier === t.key ? '#C9A962' : '#3f3f46'}`,
                            }}
                            data-testid={`demo-tier-${t.key}`}
                          >
                            <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: '#C9A962' }}>
                              {t.name}
                            </p>
                            <p className="text-[10px] text-zinc-500 mt-1">{t.frequency}</p>
                            <p className="text-[10px] text-zinc-400 mt-1">{(t.included || []).length} inclusions</p>
                          </button>
                        ))}
                      </div>
                      {(active.tiers || []).find((t) => t.key === selectedTier) && (
                        <ul className="mt-3 space-y-1 text-[12px] text-zinc-300" data-testid="demo-tier-inclusions">
                          {(active.tiers.find((t) => t.key === selectedTier)?.included || []).map((line, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span style={{ color: '#C9A962' }}>•</span>
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Pricing inputs for Strategic Enterprise */}
                    {Array.isArray(active.pricing_inputs) && active.pricing_inputs.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
                          Strategic Pricing Inputs
                        </p>
                        <div className="flex flex-wrap gap-1" data-testid="demo-pricing-inputs">
                          {active.pricing_inputs.map((k) => (
                            <span
                              key={k}
                              className="text-[10px] px-2 py-1 rounded font-mono"
                              style={{ backgroundColor: '#0f0f0f', border: '1px solid #f59e0b', color: '#fcd34d' }}
                            >
                              {k}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Contract disclaimer */}
                    <p className="text-[11px] text-zinc-500 mt-4 italic">
                      Final pricing is subject to facility assessment, scope validation and final proposal.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-zinc-500">Loading vertical…</p>
                )}
              </div>
            </div>

            {/* Steps rail */}
            <div className="mt-6 pt-4 border-t" style={{ borderColor: '#3f3f46' }}>
              <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">
                18-Step Quote Flow
              </p>
              <div className="flex flex-wrap gap-1" data-testid="demo-quote-steps">
                {QUOTE_STEPS.map((s) => (
                  <span
                    key={s}
                    className="text-[10px] px-2 py-1 rounded"
                    style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46', color: '#a1a1aa' }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* PANEL: FACILITY PROFILE */}
        {activePanel === 'facility' && (
          <Card className="p-5" style={{ backgroundColor: '#1a1a1a', border: '1px solid #3f3f46' }} data-testid="demo-facility-panel">
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: '#22d3ee' }}>Sample Facility Profile</p>
              <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Summit Corporate Tower · NECS Office Pro
              </h2>
              <p className="text-sm text-zinc-400">Sample data — for demonstration only.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { k: 'Address', v: '850 Ross Ave · Dallas, TX' },
                { k: 'Square Footage', v: '145,000 sq ft' },
                { k: 'Floors', v: '12' },
                { k: 'Suites', v: '48' },
                { k: 'Frequency', v: 'Nightly' },
                { k: 'Tier', v: 'Professional Care' },
                { k: 'Contract', v: '24 months' },
                { k: 'Payment', v: 'ACH' },
              ].map((s, i) => (
                <div key={i} className="rounded p-3" style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46' }}>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">{s.k}</p>
                  <p className="text-sm font-bold text-white mt-1">{s.v}</p>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: '#f59e0b' }}>Strategic Enterprise Sample</p>
              <h3 className="text-xl font-black text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                First Trinity Financial Center · NECS BankCare
              </h3>
              <p className="text-xs text-amber-300 italic mb-3">
                Secure and restricted-access areas are serviced only per the client's authorized security procedures.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { k: 'Branches', v: '1' },
                  { k: 'Square Footage', v: '12,500 sq ft' },
                  { k: 'Tellers', v: '8' },
                  { k: 'ATM Vestibules', v: '2' },
                  { k: 'Frequency', v: 'Nightly + Day Porter' },
                  { k: 'Tier', v: 'Executive Care' },
                  { k: 'Contract', v: '36 months' },
                  { k: 'Payment', v: 'ACH' },
                ].map((s, i) => (
                  <div key={i} className="rounded p-3" style={{ backgroundColor: '#0f0f0f', border: '1px solid #f59e0b' }}>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">{s.k}</p>
                    <p className="text-sm font-bold text-white mt-1">{s.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* PANEL: CUSTOMER PORTAL */}
        {activePanel === 'portal' && (() => {
          // Iter-150 · Red Team Fix 7 — Payment + Contract visibility for
          // Multifamily + all 12 commercial verticals. Reps can toggle
          // between property types right inside the portal panel so a
          // Sales walk-through covers every architecture.
          const portalTypes = ['residential', ...standardLines.map((l) => l.property_type), ...strategicLines.map((l) => l.property_type)];
          const displayType = portalTypes.includes(activeType) ? activeType : 'office';
          const acct = buildAccountSample(displayType);
          return (
            <Card className="p-5" style={{ backgroundColor: '#1a1a1a', border: '1px solid #3f3f46' }} data-testid="demo-portal-panel">
              <p className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: '#22d3ee' }}>
                Customer Portal · Sample
              </p>
              <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {acct.facility} · {acct.service_line}
              </h2>

              {/* Type switcher inside the portal panel */}
              <div className="flex flex-wrap gap-1 mb-4" data-testid="demo-portal-type-switcher">
                {portalTypes.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setActiveType(k)}
                    className="text-[10px] px-2 py-1 rounded font-bold uppercase tracking-widest"
                    style={{
                      backgroundColor: displayType === k ? '#C9A962' : '#0f0f0f',
                      color: displayType === k ? '#0a0a0a' : '#e5e5e5',
                      border: `1px solid ${displayType === k ? '#C9A962' : '#3f3f46'}`,
                    }}
                    data-testid={`demo-portal-type-${k}`}
                  >
                    {k === 'residential' ? 'Multifamily' : (lines.find((l) => l.property_type === k)?.necs_name || k)}
                  </button>
                ))}
              </div>

              {/* Section 22 — Payment Architecture banner */}
              <div
                className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4"
                data-testid="demo-portal-payments-banner"
              >
                <div className="rounded p-2" style={{ backgroundColor: '#0f0f0f', border: '1px solid #22d3ee' }}>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#22d3ee' }}>Stripe · Payments</p>
                  <p className="text-xs text-white font-mono">{acct.stripe_status}</p>
                </div>
                <div className="rounded p-2" style={{ backgroundColor: '#0f0f0f', border: '1px solid #10b981' }}>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#10b981' }}>QuickBooks Online · GL</p>
                  <p className="text-xs text-white font-mono">{acct.quickbooks_status}</p>
                </div>
                <div className="rounded p-2" style={{ backgroundColor: '#0f0f0f', border: '1px solid #C9A962' }}>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#C9A962' }}>NET Terms</p>
                  <p className="text-xs text-white">{acct.net_terms}</p>
                </div>
              </div>

              <div className="mb-6" data-testid="demo-portal-contract">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">Active Contract</p>
                <div className="rounded p-4" style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46' }}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      ['Service Line',    acct.service_line],
                      ['Package',         acct.package],
                      ['Duration',        `${acct.duration_months} months`],
                      ['Monthly Rate',    acct.monthly_rate],
                      ['Payment Method',  acct.payment_method],
                      ['Facility',        acct.facility],
                      ['Effective Date',  'Sep 01, 2025'],
                      ['Renewal Date',    acct.duration_months === 36 ? 'Aug 31, 2028' : (acct.duration_months === 24 ? 'Aug 31, 2027' : 'Aug 31, 2026')],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</p>
                        <p className="text-sm font-bold text-white">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-6" data-testid="demo-portal-invoices">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">Recent Invoices</p>
                <div className="rounded overflow-hidden" style={{ border: '1px solid #3f3f46' }}>
                  <table className="w-full text-sm">
                    <thead style={{ backgroundColor: '#0f0f0f' }}>
                      <tr>
                        {['Invoice #', 'Period', 'Amount', 'Status'].map((h) => (
                          <th key={h} className="text-left p-2 text-[10px] uppercase tracking-widest text-zinc-400">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {acct.invoices.map((inv) => (
                        <tr key={inv.id} className="border-t" style={{ borderColor: '#3f3f46' }}>
                          <td className="p-2 font-mono text-white">{inv.id}</td>
                          <td className="p-2 text-zinc-300">{inv.month}</td>
                          <td className="p-2 text-white font-bold">{inv.amount}</td>
                          <td className="p-2"><span className="px-2 py-0.5 rounded text-[10px]" style={{ backgroundColor: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid #10b981' }}>{inv.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div data-testid="demo-portal-payments">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-2">Payment History</p>
                <div className="rounded overflow-hidden" style={{ border: '1px solid #3f3f46' }}>
                  <table className="w-full text-sm">
                    <thead style={{ backgroundColor: '#0f0f0f' }}>
                      <tr>
                        {['Date', 'Method', 'Amount', 'Reference'].map((h) => (
                          <th key={h} className="text-left p-2 text-[10px] uppercase tracking-widest text-zinc-400">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {acct.payments.map((p, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: '#3f3f46' }}>
                          <td className="p-2 text-zinc-300">{p.date}</td>
                          <td className="p-2 text-white">{p.method}</td>
                          <td className="p-2 text-white font-bold">{p.amount}</td>
                          <td className="p-2 font-mono text-zinc-400">{p.reference}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-[10px] text-zinc-500 italic mt-4">
                Sample data · No contract, invoice or payment can be created from this demo.
              </p>
            </Card>
          );
        })()}

        {/* PANEL: RESIDENT */}
        {activePanel === 'resident' && (
          <Card className="p-5" style={{ backgroundColor: '#1a1a1a', border: '1px solid #3f3f46' }} data-testid="demo-resident-panel">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: '#22d3ee' }}>
              Multifamily Resident · Sample
            </p>
            <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Sunset Vista · Unit 101A
            </h2>
            <p className="text-sm text-zinc-400 mb-4">
              A read-only preview of the NECS Resident App. Real residents log in from their property's
              activation link.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { title: 'Tonight\'s Pickup', body: '8:00 PM · Valet Trash · Building 3', color: '#22d3ee' },
                { title: 'Compliance', body: 'Green · 12-day streak', color: '#10b981' },
                { title: 'Bulk Removal', body: 'Wed Feb 26 · Place next to dumpster', color: '#C9A962' },
              ].map((c, i) => (
                <div key={i} className="rounded p-3" style={{ backgroundColor: '#0f0f0f', border: `1px solid ${c.color}` }}>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: c.color }}>{c.title}</p>
                  <p className="text-sm text-white mt-1">{c.body}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>

      {/* Footer nudge */}
      <footer className="border-t mt-8" style={{ borderColor: '#3f3f46' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-center">
          <p className="text-xs text-zinc-400 mb-3">
            Ready to talk to NECS about your facility?
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Link to="/register-property">
              <Button size="sm" style={{ backgroundColor: '#C9A962', color: '#0a0a0a' }} data-testid="demo-footer-cta-register">
                Register Your Property
              </Button>
            </Link>
            <Link to="/contact?source=demo">
              <Button size="sm" variant="outline" data-testid="demo-footer-cta-contact">
                Contact NECS
              </Button>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
